import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sagaActions } from '../../../../../../../sagas/sagaActions';
import { getWhMetaDataReducerSlice } from '../../../../../../../redux/reducers/reducerSlices/WhMetaDataReducerSlice';
import { getRemoteConfigSelector } from '../../../../../../../redux/reducers/reducerSlices/RemoteConfigReducer';
import SSPopup from '../../../../../Common/Popup/SSPopup/SSPopup';
import _ from 'lodash-es';
import { showAlert } from '../../../../../../Assests/Utility';
import { toast } from 'react-toastify';
import SelectRescheuleReason from '../../../BookAppointments/Components/SelectReasonForRescheduleOrCancel/SelectRescheuleReason';
import ManageAppointments from '../../ManageAppointments';
import { CustomPaginationSS } from '../../../../../Common/CustomPaginationSS/CustomPaginationSS';

const AutomaticCancelPopup = (props) => {
    const {
        themeSelected,
        showPopup,
        setShowPopup,
        pendingData,
        setPendingData,
        whObj,
        onProceed,
        popupTitle,
        popupBodyText,
        cuid,
        showCheckboxes = false,
    } = props;

    const dispatch = useDispatch();
    const [selectedCancelReason, setSelectedCancelReason] = useState(null);
    const whMetaData = useSelector(getWhMetaDataReducerSlice);
    const { preCheckList, massCancelReasonList, preCheckPagination, lastPreCheckObj } = whMetaData;
    const remoteConfig = useSelector(getRemoteConfigSelector);
    const { allConfigration } = remoteConfig;
    const [reasons, setReasons] = useState([]);
    const [showRescheduleReason, setShowRescheduleReason] = useState(false);

    /**
     * Track excluded appointment PORelationIDs.
     * Empty array = all appointments are selected (will all be cancelled) → ExcludedAppointments: null to API
     * Non-empty array = those IDs are excluded from cancellation
     */
    const [excludedAppointments, setExcludedAppointments] = useState([]);

    const uniqueAppointments = _.uniqBy(preCheckList, 'PORelationID');

    // Derived pagination values (declared early so allExcluded can use totalElements)
    const { totalElements = 0, pageNo = 1, limit = 10, totalPages = 0 } = preCheckPagination || {};

    // True when ALL appointments across ALL pages have been excluded.
    // We compare against totalElements (total across all pages) so that navigating
    const effectiveTotal = totalElements > 0 ? totalElements : uniqueAppointments.length;
    // True when all appointments on the CURRENT page are excluded.
    // When pagination is active, we compare against totalElements so the user
    // cannot "exclude all" across pages — only against what's visible on the page.
    const allExcluded = showCheckboxes && excludedAppointments.length === uniqueAppointments.length && uniqueAppointments.length > 0;

    useEffect(() => {
        if (showPopup) {
            dispatch({
                type: sagaActions.MANAGE_MASS_CANCEL_REASON,
                payload: {
                    CRUDTYPE: 'R',
                    IDWarehouse: whObj.ID,
                    UserName: cuid
                }
            });
            // Default: all appointments selected (none excluded)
            setExcludedAppointments([]);
            setSelectedCancelReason(null);
        }
    }, [showPopup]);

    useEffect(() => {
        if (massCancelReasonList?.length > 0) {
            setReasons(massCancelReasonList);
        } else if (allConfigration['ReasonForRescheduleOrCancel']?.length) {
            const fallbackReasons = allConfigration['ReasonForRescheduleOrCancel']
                .slice(1, -1)
                .split(',')
                .map(reason => ({ ReasonDescription: reason }));
            setReasons(fallbackReasons);
        }
    }, [massCancelReasonList, allConfigration]);

    /**
     * Toggle a single appointment's exclusion state.
     * Unchecking adds to excludedAppointments; re-checking removes it.
     */
    const handleCheckboxChange = (appointment) => {
        const id = appointment.AppointmentNumber;
        setExcludedAppointments(prev =>
            prev.includes(id)
                ? prev.filter(i => i !== id)  // re-include → remove from excluded
                : [...prev, id]        // exclude → add to excluded
        );
    };

    /**
     * Select-all / deselect-all handler for the ManageAppointments header checkbox.
     */
    const handleSelectAll = () => {
        if (excludedAppointments.length === 0) {
            // All currently selected → deselect all (exclude all on current page)
            setExcludedAppointments(uniqueAppointments.map(a => a.AppointmentNumber));
        } else {
            // Some or all excluded → select all (clear exclusions)
            setExcludedAppointments([]);
        }
    };

    /**
     * Page change handler — dispatches PRE_CHECK_FOR_ADMIN_OPERATION with the
     * new pageNo/limit so the saga fetches the next page and updates preCheckList.
     */
    const handlePageChange = (pageNo, limit) => {
        if (!lastPreCheckObj || _.isEmpty(lastPreCheckObj)) return;
        dispatch({
            type: sagaActions.PRE_CHECK_FOR_ADMIN_OPERATION,
            payload: {
                ...lastPreCheckObj,
                pageNo,
                limit,
            },
        });
    };

    const handleConfirm = (text) => {
        if (text === 'Yes') {
            // When all appointments are excluded, no cancellation needed — skip reason validation
            // but still call onProceed so the parent can proceed with creating the SAB
            if (allExcluded) {
                setShowPopup(false);
                if (pendingData) {
                    onProceed(null, excludedAppointments);
                }
                setSelectedCancelReason(null);
                setExcludedAppointments([]);
                return;
            }

            // Only require a reason when there are appointments to cancel
            if (!selectedCancelReason) {
                showAlert(toast.TYPE.ERROR, 'Please select a reason for cancellation.', themeSelected);
                return;
            }

            setShowPopup(false);
            if (pendingData) {
                // If no exclusions, send null (all will be cancelled); otherwise send the excluded IDs array
                const excluded = excludedAppointments.length === 0 ? null : excludedAppointments;
                onProceed(selectedCancelReason, excluded);
            }
        } else {
            setShowPopup(false);
            dispatch({ type: sagaActions.UPDATE_PRE_CHECK_LIST, payload: [] });
            setPendingData(null);
            setSelectedCancelReason(null);
            setExcludedAppointments([]);
        }
    };

    const showPagination = showCheckboxes && totalElements > limit;

    const popupObj = {
        id: 'conflictAppointmentsPopup',
        modalLabel: 'conflictAppointmentsPopup',
        showClose: true,
        showfooter: true,
        showHeader: true,
        modalSize: 'xl',
        title: popupTitle,
        bodyText: (
            <div>
                <p className="text-center">
                    {popupBodyText}
                </p>
                {showCheckboxes && (
                    <ManageAppointments
                        isEmbedded={true}
                        hideActions={true}
                        themeSelected={themeSelected}
                        showCheckboxes={true}
                        excludedAppointments={excludedAppointments}
                        onCheckboxChange={handleCheckboxChange}
                        onSelectAll={handleSelectAll}
                    />
                )}
                {/* Pagination control — only visible when there are more rows than one page */}
                {showPagination && (
                    <CustomPaginationSS
                        pageChange={handlePageChange}
                        pageNo={pageNo}
                        pageSize={limit}
                        totalElements={totalElements}
                        totalPages={totalPages}
                        last={pageNo >= totalPages}
                        first={pageNo === 1}
                        showTotalCount={true}
                        showFirstPageBtn={true}
                        showLastPageBtn={true}
                        paginationLimitArray={[10, 25, 50]}
                    />
                )}
                {showCheckboxes && excludedAppointments.length > 0 && (
                    <p className="mt-2 text-muted f-12 text-center">
                        {excludedAppointments.length} appointment{excludedAppointments.length > 1 ? 's' : ''} excluded from cancellation.
                    </p>
                )}
                <p className="mt-3 text-center fw-bold">Do you want to proceed with cancelling the selected appointments?</p>
                <div className='row d-flex justify-content-center my-3'>
                    <div className='col-6'>
                        {/* Reason selector is only required when there are appointments to cancel */}
                        {!allExcluded && (
                            <SelectRescheuleReason
                                rescheduleReason={selectedCancelReason}
                                setRescheduleReason={setSelectedCancelReason}
                                onSubmit={() => { setShowRescheduleReason(false); }}
                                type='Cancel'
                                errorMsg={null}
                                IDWarehouse={whObj.ID}
                                showTitle={false}
                            />
                        )}
                        {allExcluded && (
                            <p className="text-center text-muted f-12">
                                No appointments selected for cancellation. Click Proceed to continue without cancelling any appointments.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        ),
        subText: "",
        keyboard: false,
        backdrop: "static",
        footerBtn: {
            btn1: {
                show: true,
                text: "Proceed",
                // Disable only when there are appointments to cancel but no reason is selected
                isDisabled: !allExcluded && !selectedCancelReason,
            },
            btn2: {
                show: true,
                text: "Cancel",
            }
        },
    };
    popupObj.alignType = 'end';
    popupObj.headerAlign = 'start';

    return (
        <SSPopup
            {...popupObj}
            popupBtnHandler={(text) => handleConfirm(text === 'Proceed' ? 'Yes' : 'No')}
            closepopup={() => handleConfirm('No')}
            themeSelected={themeSelected}
            showModal={showPopup}
        />
    );
};

export default AutomaticCancelPopup;
